import React, { FC } from "react";
import { useTranslation } from "i18n";
import { ReactComponent as OpenInNewTabIcon } from "component-library/src/images/svg/icons/open-in-new-tab.svg";

import { IconTextLink, Pill } from "component-library";
import { dataTestIds } from "data-testids";
import {
  AddOnCodes,
  addOnMoreInfoLinkMap,
  addOnNamesMap,
} from "component-library/src/components/Organisms/Billing/constants";
import { ReactComponent as CrossIcon } from "component-library/src/images/svg/icons/cross-thin.svg";
import {
  AddOnCalculatedStatus,
  buyAddOnButtonNamesMap,
  SubscriptionState,
} from "app/constants";
import { IAddOn } from "services/api/BillingApi/BillingApi";
import { getStatusPillClassNames } from "components/StatusPill/StatusPill";
import SectionBox from "components/SectionBox";
interface IAddOnSectionProps {
  addOn: IAddOn;
  onCancelAddOnClick: (addOnCode: string) => void;
  onBuyAddOnClick: (code: AddOnCodes) => void;
  "data-testid"?: string;
  subscriptionState: SubscriptionState;
}

const AddOnSection: FC<React.PropsWithChildren<IAddOnSectionProps>> = ({
  addOn,
  onCancelAddOnClick,
  onBuyAddOnClick,
  "data-testid": dataTestId,
  subscriptionState,
}) => {
  const { t } = useTranslation();
  const { description, calculatedStatus, availableToBuy, code, cancelled } =
    addOn;
  const isActive = calculatedStatus === AddOnCalculatedStatus.Active;

  const showCancelButton = isActive && !cancelled;
  const isAvailable =
    !showCancelButton && !cancelled && !isActive && availableToBuy;

  const link = addOnMoreInfoLinkMap[addOn.code];

  return (
    <SectionBox
      title={t(addOnNamesMap[code])}
      description={description}
      bgColour="bg-color-neutral-three"
      pill={
        <Pill
          size="md"
          className={getStatusPillClassNames(isActive, false, false)}
          theme="light"
        >
          {isActive ? t("common:label.active") : t("common:label.inactive")}
        </Pill>
      }
      primaryButton={
        link && (
          <div className="absolute flex items-center self-start flex-1 gap-1 right-2 md:static whitespace-nowrap">
            <a
              href={link}
              target="_blank"
              className="text-sm font-semibold leading-snug md:text-base"
              rel="noreferrer"
            >
              {t("common:label.moreInformation")}
            </a>
            <OpenInNewTabIcon className="text-color-brand-one" />
          </div>
        )
      }
      secondaryButton={
        isAvailable && (
          <IconTextLink
            text={t(buyAddOnButtonNamesMap[code])}
            variant="button-icon-text-secondary"
            svgIconStart={
              <CrossIcon className="rotate-45" height={20} width={20} />
            }
            data-testid={
              dataTestIds.userAccountApp["MySubscription.BuyAddOn.button"]
            }
            handleClick={() => onBuyAddOnClick(code)}
          />
        )
      }
      tertiaryButton={
        showCancelButton &&
        subscriptionState !== SubscriptionState.Canceled && (
          <IconTextLink
            text={t("common:button.label.endAddOnSubscription").toUpperCase()}
            variant="button-icon-text"
            handleClick={() => onCancelAddOnClick(addOn.code)}
            className="text-color-brand-one pl-[15px] pr-[15px]"
          />
        )
      }
      data-testid={dataTestId}
    />
  );
};

export default AddOnSection;
